import { Button, ButtonGroup, FormControlLabel, Switch } from '@mui/material';
import { format } from 'date-fns';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components/macro';

import Avatar from 'components/Avatar/Avatar';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { MessageSourcesE, MessageTypesE } from 'declarations/enums/messages';
import { TicketActionTypesE } from 'declarations/enums/tickets';
import type { TicketDetailsI } from 'declarations/interfaces/tickets';
import type { NoopT } from 'declarations/types/common';
import { getUserPlaceholderName } from 'helpers/user';

import MessageForm from './components/MessageForm/MessageForm';
import MessagesLoader from './components/MessagesLoader/MessagesLoader';
import { useMessages } from './contexts/MessagesContext';
import {
  ActionMessageHeaderStyled,
  ActionMessageSenderNameStyled,
  ActionMessageSentDateStyled,
  ActionMessageStyled,
  AttachmentNameStyled,
  CountdownStyled,
  FlexContainerStyled,
  MessageContentStyled,
  MessageOptionsWrapStyled,
  MessageSenderNameStyled,
  MessageSentDateStyled,
  MessageStyled,
  MessageWrapStyled,
  MessagesCardBottomStyled,
  MessagesCardInnerStyled,
  MessagesScrollAreaStyled,
  MessagesWrapStyled,
} from './Messages.styled';

export type MessagesPropsT = {
  roofTopName: string;
  requiresAttachment: TicketDetailsI['requiresAttachment'];
  handleOpenAddAttachmentPopup: NoopT;
  onAssignToWarrCloudChanged: (checked: boolean) => void;
};

const Messages: FC<MessagesPropsT> = ({
  roofTopName,
  requiresAttachment,
  handleOpenAddAttachmentPopup,
  onAssignToWarrCloudChanged,
}) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const { userProfile } = useUserProfile();
  const {
    messages,
    isLoadingMessages,
    undoMessageTimeLeft,
    unsavedMessage,
    handleUndoMessage,
    handleConfirmMessage,
  } = useMessages();
  const messagesWrapperRef = useRef<HTMLDivElement>(null);

  /**
   * Hooks
   */
  const [assignToWarrCloud, setAssignToWarrCloud] = useState<boolean>(true);

  /**
   * Handlers
   */
  const handleAssignToWarrCloudChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked;
    setAssignToWarrCloud(checked);
    onAssignToWarrCloudChanged(checked);
  };

  const handleUrlPreviewClick = (imageUrl: string): void => {
    window.open(imageUrl, '_blank', 'noopener, noreferrer');
  };

  /**
   * Effects
   */
  useEffect(() => {
    if (messagesWrapperRef.current && (messages.length || unsavedMessage)) {
      const { current: messagesWrapper } = messagesWrapperRef;

      messagesWrapper.scrollTop = messagesWrapper.scrollHeight;
    }
  }, [messages, unsavedMessage]);

  return (
    <MessagesCardInnerStyled>
      <MessagesScrollAreaStyled ref={messagesWrapperRef}>
        <MessagesWrapStyled>
          {isLoadingMessages && messages.length === 0 ? (
            <MessagesLoader />
          ) : (
            messages.map(
              (
                {
                  action,
                  typeId,
                  sender,
                  sentDate,
                  text,
                  attachmentName = '',
                  attachmentText = '',
                  attachmentUrl = '',
                  sourceId,
                },
                i
              ) => {
                const [freeText] = text.split('{');
                const [senderFirstName, senderLastName] = sender.displayName
                  ? sender.displayName.split(' ')
                  : [];
                const isLoggedInUser = sender.userId === userProfile?.userId;

                const isTextMessage = typeId === MessageTypesE.Text;

                const isAttachmentTypeMessage =
                  typeId === MessageTypesE.ArtifactUpload ||
                  typeId === MessageTypesE.ArtifactDelete ||
                  typeId === MessageTypesE.ScreenshotUpload ||
                  typeId === MessageTypesE.AttachmentApproval;

                const isGetApprovalTypeMessage =
                  typeId === MessageTypesE.Action &&
                  action?.typeId === TicketActionTypesE.GetApproval &&
                  action?.privateMessage;

                const isPrivateMsgShown =
                  typeId === MessageTypesE.Action &&
                  action?.typeId !== TicketActionTypesE.GetApproval &&
                  action?.privateMessage;

                const isScheduleImportMessage = sourceId === MessageSourcesE.ScheduleImport;

                const isActionMessage = typeId !== MessageTypesE.Text;
                const hasThumbnail = attachmentUrl.length > 0;
                const isAssigneeChangeTypeMessage =
                  typeId === MessageTypesE.Action &&
                  action?.typeId === TicketActionTypesE.ChangeAssigneeId;

                const getJustifyValue = (): 'start' | 'end' | 'center' => {
                  if (isActionMessage) return 'center';
                  return isLoggedInUser ? 'end' : 'start';
                };

                return (
                  <MessageWrapStyled
                    data-testid="message"
                    key={`message-${i}`}
                    justify={getJustifyValue()}
                    align="start"
                  >
                    {!isActionMessage && !isLoggedInUser && (
                      <Avatar
                        size="md"
                        placeholder={getUserPlaceholderName(senderFirstName, senderLastName)}
                        imageUrl={sender.photoUrl}
                      />
                    )}
                    <FlexContainerStyled
                      direction="column"
                      align="center"
                      isLoggedInUser={isLoggedInUser}
                      hasThumbnail={hasThumbnail}
                    >
                      {isActionMessage && (
                        <ActionMessageHeaderStyled
                          direction="row"
                          align="center"
                          justify="start"
                          gap="0.25rem"
                        >
                          <ActionMessageSenderNameStyled>
                            {isScheduleImportMessage && (
                              <Typography variant="body4" color="red" isInline={true}>
                                [Import]&nbsp;
                              </Typography>
                            )}
                            <Typography
                              variant="body3"
                              isInline={true}
                              color={theme.mode === 'dark' ? 'light' : undefined}
                            >
                              {sender.displayName}
                            </Typography>
                          </ActionMessageSenderNameStyled>
                          <ActionMessageSentDateStyled size="xxs">
                            {format(new Date(sentDate), 'p, MMM d, yyyy')}
                          </ActionMessageSentDateStyled>
                        </ActionMessageHeaderStyled>
                      )}
                      <MessageContentStyled
                        isActionMessageContent={isActionMessage}
                        isLoggedInUser={isLoggedInUser}
                        hasThumbnail={hasThumbnail}
                      >
                        {!isActionMessage ? (
                          <>
                            <FlexContainer align="start">
                              <MessageSenderNameStyled>
                                {isScheduleImportMessage && (
                                  <Typography variant="body3" color="red" isInline={true}>
                                    [Import]&nbsp;
                                  </Typography>
                                )}
                                {!isLoggedInUser && (
                                  <Typography
                                    variant="body2"
                                    isInline={true}
                                    color={theme.mode === 'dark' ? 'light' : undefined}
                                  >
                                    {sender.displayName}
                                  </Typography>
                                )}
                              </MessageSenderNameStyled>
                              <MessageSentDateStyled size="xs">
                                {format(new Date(sentDate), 'p, MMM d, yyyy')}
                              </MessageSentDateStyled>
                            </FlexContainer>
                            <MessageStyled
                              size="lg"
                              isText={isTextMessage}
                              isLoggedInUser={isLoggedInUser}
                            >
                              {text}
                              {!isAssigneeChangeTypeMessage && (
                                <AttachmentNameStyled>
                                  {action?.involvedUser?.displayName}
                                </AttachmentNameStyled>
                              )}
                            </MessageStyled>
                          </>
                        ) : (
                          <>
                            <FlexContainer
                              align="center"
                              direction={hasThumbnail ? 'column' : 'row'}
                            >
                              {isAttachmentTypeMessage ? (
                                <ActionMessageStyled
                                  size="lg"
                                  hasThumbnail={hasThumbnail}
                                  isLoggedInUser={isLoggedInUser}
                                >
                                  {typeId !== MessageTypesE.ScreenshotUpload ? freeText : null}
                                  {attachmentUrl && (
                                    <img
                                      className="image-preview"
                                      src={attachmentUrl}
                                      alt={attachmentName}
                                      onClick={(): void => handleUrlPreviewClick(attachmentUrl)}
                                      style={{ maxHeight: '120px', maxWidth: '100%' }}
                                    />
                                  )}
                                  <AttachmentNameStyled>{attachmentName}</AttachmentNameStyled>
                                  {attachmentText}
                                </ActionMessageStyled>
                              ) : (
                                <>
                                  <ActionMessageStyled size="lg">
                                    {freeText}&nbsp;
                                    {!isAssigneeChangeTypeMessage && (
                                      <AttachmentNameStyled>
                                        {action?.involvedUser?.displayName}
                                      </AttachmentNameStyled>
                                    )}
                                  </ActionMessageStyled>
                                  {isPrivateMsgShown && (
                                    <Typography color="darkBlue">
                                      &nbsp;Private: {action.privateMessage}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </FlexContainer>
                            {isGetApprovalTypeMessage && (
                              <ActionMessageStyled size="lg">
                                <AttachmentNameStyled>{action.privateMessage}</AttachmentNameStyled>
                              </ActionMessageStyled>
                            )}
                          </>
                        )}
                      </MessageContentStyled>
                    </FlexContainerStyled>
                  </MessageWrapStyled>
                );
              }
            )
          )}

          {unsavedMessage && (
            <MessageWrapStyled data-testid="unsaved-message" justify="end" align="start">
              <FlexContainer direction="column" align="end">
                <MessageContentStyled isLoggedInUser>
                  <FlexContainer justify="end">
                    <MessageSentDateStyled size="xs">
                      {format(unsavedMessage.sentDate, 'p, MMM d, yyyy')}
                    </MessageSentDateStyled>
                  </FlexContainer>
                  <MessageStyled size="lg" isText isLoggedInUser>
                    {unsavedMessage.text}
                  </MessageStyled>
                </MessageContentStyled>
                <FlexContainer
                  direction={isMobile ? 'column-reverse' : 'row'}
                  align="center"
                  justify="end"
                >
                  <CountdownStyled size="xs">{undoMessageTimeLeft} sec</CountdownStyled>
                  <ButtonGroup
                    sx={{ borderRadius: 10, padding: '0.5rem 1rem', height: 45 }}
                    size="small"
                  >
                    <Button
                      title="Undo message"
                      aria-label="Undo message"
                      onClick={handleUndoMessage}
                      sx={{ borderRadius: 50, padding: '0.5rem 1rem', width: 50 }}
                      size="small"
                    >
                      Undo
                    </Button>
                    <Button
                      title="Send message now"
                      aria-label="Confirm message"
                      onClick={(): void => handleConfirmMessage(assignToWarrCloud)}
                      sx={{ borderRadius: 10, padding: '0.5rem 1rem', width: 75 }}
                      size="small"
                    >
                      Confirm
                    </Button>
                  </ButtonGroup>
                </FlexContainer>
              </FlexContainer>
            </MessageWrapStyled>
          )}
        </MessagesWrapStyled>
      </MessagesScrollAreaStyled>

      <MessagesCardBottomStyled>
        <MessageOptionsWrapStyled align="center" justify="end">
          <FormControlLabel
            control={
              <Switch
                checked={assignToWarrCloud}
                onChange={handleAssignToWarrCloudChange}
                color="primary"
              />
            }
            label="Assign to Dealer"
            sx={{ mr: 0.5 }}
          />
        </MessageOptionsWrapStyled>

        <MessageForm
          requiresAttachment={requiresAttachment}
          roofTopName={roofTopName}
          assignToWarrCloud={assignToWarrCloud}
          handleOpenAddAttachmentPopup={handleOpenAddAttachmentPopup}
        />
      </MessagesCardBottomStyled>
    </MessagesCardInnerStyled>
  );
};

export default Messages;
